import { type ConsoleRole, hasConsoleRole } from '@plato/shared';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { ConsoleData, TenantSummary } from './console-data.ts';

interface ConsoleContextValue {
  readonly data: ConsoleData;
  readonly tenant: TenantSummary;
}

const ConsoleContext = createContext<ConsoleContextValue | null>(null);

export function ConsoleProvider(props: {
  data: ConsoleData;
  tenant: TenantSummary;
  children: ReactNode;
}) {
  return (
    <ConsoleContext.Provider value={{ data: props.data, tenant: props.tenant }}>
      {props.children}
    </ConsoleContext.Provider>
  );
}

export function useConsole(): ConsoleContextValue {
  const context = useContext(ConsoleContext);
  if (context === null) throw new Error('useConsole needs a ConsoleProvider');
  return context;
}

export function useHasRole(role: ConsoleRole): boolean {
  return hasConsoleRole(useConsole().tenant.role, role);
}

export type Query<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly value: T };

// `load` is an effect dependency: pass a module-level function, or every render reloads.
export function useTenantQuery<T>(load: (data: ConsoleData, tenant: TenantSummary) => Promise<T>) {
  return useReloadableTenantQuery(load).query;
}

export function useReloadableTenantQuery<T>(
  load: (data: ConsoleData, tenant: TenantSummary) => Promise<T>,
): { query: Query<T>; reload: () => void } {
  const { data, tenant } = useConsole();
  const [query, setQuery] = useState<Query<T>>({ status: 'loading' });
  const [version, setVersion] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `version` is the reload trigger.
  useEffect(() => {
    let current = true;
    load(data, tenant).then(
      (value) => current && setQuery({ status: 'ready', value }),
      (error: unknown) =>
        current &&
        setQuery({ status: 'error', message: error instanceof Error ? error.message : 'Failed' }),
    );
    return () => {
      current = false;
    };
  }, [load, data, tenant, version]);

  const reload = useCallback(() => setVersion((value) => value + 1), []);
  return { query, reload };
}
